<script setup>
// NOTICE: KAPTION-INTEGRATION
// two-step-verification.PASSKEY.1 — "Add a passkey" (canvas: Passkey).
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import KaptionTwoStepAPI from 'dashboard/api/kaptionTwoStep';
import {
  createPasskey,
  isPasskeyCancel,
  isPasskeySupported,
} from 'shared/helpers/kaptionWebauthn';

const emit = defineEmits(['added']);
const { t } = useI18n();

const dialogRef = ref(null);
const nickname = ref('');
const busy = ref(false);
const error = ref('');

const guessDeviceName = () => {
  const ua = navigator.userAgent || '';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return t('KAPTION_TWO_STEP.PASSKEY_DIALOG.NAME_DEFAULT');
};

const open = () => {
  nickname.value = guessDeviceName();
  error.value = isPasskeySupported()
    ? ''
    : t('KAPTION_TWO_STEP.PASSKEY_DIALOG.UNSUPPORTED');
  busy.value = false;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const create = async () => {
  if (busy.value || !isPasskeySupported()) return;
  busy.value = true;
  error.value = '';
  try {
    const { data: options } = await KaptionTwoStepAPI.passkeyOptions();
    const credential = await createPasskey(options);
    const { data } = await KaptionTwoStepAPI.createPasskey(
      credential,
      nickname.value
    );
    close();
    emit('added', { method: 'passkey', status: data });
  } catch (e) {
    if (isPasskeyCancel(e)) {
      error.value = t('KAPTION_TWO_STEP.PASSKEY_DIALOG.CANCELLED');
    } else if (e?.response?.data?.error_code === 'not_configured') {
      error.value = t('KAPTION_TWO_STEP.PASSKEY_DIALOG.NOT_CONFIGURED');
    } else {
      error.value =
        e?.response?.data?.error || t('KAPTION_TWO_STEP.ERRORS.GENERIC');
    }
  } finally {
    busy.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @confirm="create"
  >
    <div class="flex flex-col gap-5" data-testid="kpt-two-step-passkey-dialog">
      <div class="flex flex-col gap-2">
        <span
          class="flex items-center justify-center mb-1 rounded-full size-10 outline outline-1 outline-n-weak"
        >
          <Icon icon="i-lucide-key-round" class="size-5 text-n-slate-10" />
        </span>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('KAPTION_TWO_STEP.PASSKEY_DIALOG.TITLE') }}
        </h3>
        <p class="text-sm leading-relaxed text-n-slate-11">
          {{ $t('KAPTION_TWO_STEP.PASSKEY_DIALOG.DESC') }}
        </p>
      </div>
      <ul
        class="flex flex-col gap-2.5 p-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2/50 text-n-slate-12"
      >
        <li class="flex items-center gap-2">
          <Icon icon="i-lucide-scan-face" class="size-4 text-n-teal-11" />
          {{ $t('KAPTION_TWO_STEP.PASSKEY_DIALOG.POINT_APPLE') }}
        </li>
        <li class="flex items-center gap-2">
          <Icon icon="i-lucide-fingerprint" class="size-4 text-n-teal-11" />
          {{ $t('KAPTION_TWO_STEP.PASSKEY_DIALOG.POINT_WINDOWS') }}
        </li>
        <li class="flex items-center gap-2">
          <Icon icon="i-lucide-usb" class="size-4 text-n-teal-11" />
          {{ $t('KAPTION_TWO_STEP.PASSKEY_DIALOG.POINT_KEY') }}
        </li>
      </ul>
      <Input
        v-model="nickname"
        :label="$t('KAPTION_TWO_STEP.PASSKEY_DIALOG.NAME_LABEL')"
        maxlength="60"
        data-testid="kpt-two-step-passkey-name"
      />
      <p v-if="error" class="text-sm text-n-ruby-11" role="alert">
        {{ error }}
      </p>
    </div>
    <template #footer>
      <div class="flex gap-3">
        <Button
          type="button"
          slate
          faded
          class="flex-1"
          :label="$t('KAPTION_TWO_STEP.ACTIONS.CANCEL')"
          @click="close"
        />
        <Button
          type="submit"
          class="flex-1"
          :label="$t('KAPTION_TWO_STEP.PASSKEY_DIALOG.CREATE')"
          :is-loading="busy"
          :disabled="busy || !nickname.trim()"
          data-testid="kpt-two-step-passkey-create"
        />
      </div>
    </template>
  </Dialog>
</template>
